import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Briefcase, Bell, Info, Zap, AlertTriangle } from 'lucide-react';
import api from '../services/api';

const EVENT_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;      // text colour
  dot: string;        // dot background
  badge: string;      // pill background + text
}> = {
  signal: {
    icon: TrendingUp,
    color: 'text-teal-400',
    dot: 'bg-teal-400',
    badge: 'bg-teal-500/15 text-teal-300',
  },
  banker_lead: {
    icon: Briefcase,
    color: 'text-amber-400',
    dot: 'bg-amber-400',
    badge: 'bg-amber-500/15 text-amber-300',
  },
  alert: {
    icon: AlertTriangle,
    color: 'text-red-400',
    dot: 'bg-red-400',
    badge: 'bg-red-500/15 text-red-300',
  },
  signal_queue: {
    icon: Zap,
    color: 'text-purple-400',
    dot: 'bg-purple-400',
    badge: 'bg-purple-500/15 text-purple-300',
  },
  notification: {
    icon: Bell,
    color: 'text-blue-400',
    dot: 'bg-blue-400',
    badge: 'bg-blue-500/15 text-blue-300',
  },
};

const DEFAULT_CONFIG = {
  icon: Info,
  color: 'text-slate-400',
  dot: 'bg-slate-400',
  badge: 'bg-slate-500/15 text-slate-300',
};

function getConfig(eventType: string) {
  return EVENT_CONFIG[eventType] ?? DEFAULT_CONFIG;
}

function directionColor(dir?: string | null) {
  if (!dir) return '';
  const d = dir.toUpperCase();
  if (d === 'BUY') return 'text-emerald-400';
  if (d === 'SELL') return 'text-red-400';
  return 'text-slate-400';
}

function relativeTime(ts: string) {
  const parsed = new Date(ts);
  if (isNaN(parsed.getTime())) return '';
  const diff = Date.now() - parsed.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface ActivityFeedProps {
  limit?: number;
  hours?: number;
  maxHeight?: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  limit = 20,
  hours = 24,
  maxHeight = '360px',
}) => {
  const navigate = useNavigate();

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['activity-feed', limit, hours],
    queryFn: () => api.getActivityFeed(limit, hours),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-slate-700 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 bg-slate-700 rounded w-3/4" />
              <div className="h-2.5 bg-slate-700/60 rounded w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-500">
        <Zap size={28} className="mb-2 opacity-40" />
        <p className="text-sm">No banker leads above 80% in the last {hours}h</p>
      </div>
    );
  }

  return (
    <div
      className="overflow-y-auto pr-1 space-y-1"
      style={{ maxHeight }}
    >
      {events.map((event, idx) => {
        const cfg = getConfig(event.event_type);
        const Icon = cfg.icon;

        return (
          <button
            key={event.id}
            type="button"
            onClick={() => event.link && navigate(event.link)}
            className={`w-full text-left flex gap-3 items-start px-3 py-2.5 rounded-lg transition-all duration-200
              hover:bg-slate-700/40 active:scale-[0.99]
              ${event.link ? 'cursor-pointer' : 'cursor-default'}
              animate-fade-in`}
            style={{ animationDelay: `${idx * 30}ms` }}
          >
            {/* Icon circle */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5
              bg-slate-800 border border-slate-700/60`}>
              <Icon size={14} className={cfg.color} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-semibold text-slate-200 truncate">{event.title}</p>
                {event.direction && (
                  <span className={`text-[10px] font-bold uppercase ${directionColor(event.direction)}`}>
                    {event.direction}
                  </span>
                )}
                {event.confidence != null && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.badge}`}>
                    {Math.round(event.confidence)}%
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2 leading-snug">
                {event.description}
              </p>
            </div>

            {/* Timestamp + live dot */}
            <div className="flex flex-col items-end gap-1 shrink-0">
              <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot} opacity-70`} />
              <span className="text-[10px] text-slate-500 whitespace-nowrap">
                {relativeTime(event.timestamp)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ActivityFeed;
